import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FaTrophy, FaCalendarCheck, FaMountain, FaArrowRight } from 'react-icons/fa';

const BRAND = { blue: '#043663', red: '#D52B1E' };

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  min-height: min(70vh, 640px);
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.35s ease-out;
`;

const Intro = styled.header`
  position: relative;
  padding: 1.35rem 1.4rem 1.45rem;
  margin-bottom: 1.35rem;
  border-radius: 16px;
  background: linear-gradient(145deg, #043663 0%, #0a4d82 55%, #032842 100%);
  color: #fff;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(4, 54, 99, 0.22);

  &::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: rgba(213, 43, 30, 0.22);
    pointer-events: none;
  }
`;

const IntroEyebrow = styled.p`
  margin: 0 0 0.4rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
`;

const Title = styled.h1`
  margin: 0 0 0.45rem 0;
  font-size: clamp(1.55rem, 3.5vw, 2.05rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #fff;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 36rem;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.55;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  flex: 1;
  @media (min-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
    align-items: stretch;
  }
`;

const ChoiceBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  text-align: left;
  padding: 1.25rem 1.2rem 1.3rem;
  min-height: 200px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  cursor: pointer;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 12px 28px rgba(15, 23, 42, 0.05);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => (p.$accent === 'red' ? BRAND.red : BRAND.blue)};
    opacity: 0.9;
  }

  &:hover {
    border-color: ${(p) => (p.$accent === 'red' ? 'rgba(213, 43, 30, 0.35)' : 'rgba(4, 54, 99, 0.35)')};
    box-shadow: 0 10px 28px rgba(4, 54, 99, 0.12);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid ${BRAND.blue};
    outline-offset: 2px;
  }
`;

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 12px;
  background: ${(p) =>
    p.$muted
      ? 'linear-gradient(145deg, #e2e8f0, #f1f5f9)'
      : p.$accent === 'red'
        ? 'linear-gradient(145deg, #D52B1E, #b91c1c)'
        : 'linear-gradient(145deg, #043663, #0a5a9e)'};
  color: ${(p) => (p.$muted ? BRAND.blue : '#fff')};
  font-size: 1.05rem;
  flex-shrink: 0;
`;

const ChoiceLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const ChoiceHint = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
  flex: 1;
`;

const ChoiceFooter = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: auto;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${BRAND.blue};
`;

const Badge = styled.span`
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0f766e;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
`;

const FootNote = styled.p`
  margin: 1.5rem 0 0;
  padding: 0.95rem 1.1rem;
  border-radius: 12px;
  background: rgba(4, 54, 99, 0.04);
  border: 1px solid rgba(4, 54, 99, 0.08);
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.5;
`;

/**
 * Events landing: pick Leagues, Tournaments, or Camps.
 */
export default function PlayerEventsHub({ onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const go = (area) => {
    if (typeof onSelect === 'function') {
      onSelect(area);
      return;
    }
    navigate(`/player/tournaments?area=${encodeURIComponent(area)}`);
  };

  return (
    <Wrap>
      <Intro>
        <IntroEyebrow>{t('playerOrganisedEvents.hubEyebrow', 'Helvetic Padel Series')}</IntroEyebrow>
        <Title>{t('playerOrganisedEvents.eventsPageTitle', 'Events')}</Title>
        <Subtitle>
          {t(
            'playerOrganisedEvents.hubSubtitle',
            'Choose what you want to explore. Leagues is your season competition; Tournaments are bookable one-off events.'
          )}
        </Subtitle>
      </Intro>

      <Grid>
        <ChoiceBtn type="button" $accent="blue" onClick={() => go('leagues')}>
          <IconTile $accent="blue" aria-hidden>
            <FaTrophy />
          </IconTile>
          <ChoiceLabel>{t('playerOrganisedEvents.leaguesSection', 'Leagues')}</ChoiceLabel>
          <ChoiceHint>
            {t(
              'playerOrganisedEvents.hubLeaguesHint',
              'Discover editions, create or join teams, and open your league dashboard.'
            )}
          </ChoiceHint>
          <ChoiceFooter>
            {t('playerOrganisedEvents.hubOpen', 'Open')} <FaArrowRight size={11} aria-hidden />
          </ChoiceFooter>
        </ChoiceBtn>

        <ChoiceBtn type="button" $accent="red" onClick={() => go('tournaments')}>
          <IconTile $accent="red" aria-hidden>
            <FaCalendarCheck />
          </IconTile>
          <ChoiceLabel>{t('playerOrganisedEvents.tournamentsTitle', 'Tournaments')}</ChoiceLabel>
          <ChoiceHint>
            {t(
              'playerOrganisedEvents.hubTournamentsHint',
              'Browse and book admin-organised tournaments.'
            )}
          </ChoiceHint>
          <ChoiceFooter>
            {t('playerOrganisedEvents.hubBrowse', 'Browse')} <FaArrowRight size={11} aria-hidden />
          </ChoiceFooter>
        </ChoiceBtn>

        <ChoiceBtn type="button" $accent="blue" onClick={() => go('camps')}>
          <IconTile $muted aria-hidden>
            <FaMountain />
          </IconTile>
          <ChoiceLabel>{t('playerOrganisedEvents.campsTitle', 'Camps')}</ChoiceLabel>
          <ChoiceHint>
            {t(
              'playerOrganisedEvents.hubCampsHint',
              'Training camps and clinics — coming soon.'
            )}
          </ChoiceHint>
          <Badge>{t('playerOrganisedEvents.comingSoon', 'Coming soon')}</Badge>
        </ChoiceBtn>
      </Grid>

      <FootNote>
        {t(
          'playerOrganisedEvents.hubFootnote',
          'Everything competition-related lives under Events — seasons and standings in Leagues, one-off bookings in Tournaments, and camps when they open.'
        )}
      </FootNote>
    </Wrap>
  );
}
